import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGift, FaCopy, FaShareAlt, FaWallet, FaTimes, FaCheck } from 'react-icons/fa';
import toast from 'react-hot-toast';

const OPERATORS = [
  { key: 'orange_money', label: 'Orange Money' },
  { key: 'mtn_momo',    label: 'MTN MoMo' },
  { key: 'wave',        label: 'Wave' },
];

function ReferralSection({ referral, onWithdraw }) {
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [withdrawForm, setWithdrawForm]         = useState({ amount: '', operator: '', phone: '' });
  const [submitting, setSubmitting]             = useState(false);

  const balance    = referral.balanceFcfa ?? 0;
  const canWithdraw = referral.canWithdraw;
  const minW       = referral.minWithdrawal ?? 2000;

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!withdrawForm.amount || !withdrawForm.operator || !withdrawForm.phone) {
      toast.error('Tous les champs sont obligatoires');
      return;
    }
    if (parseInt(withdrawForm.amount) < minW) {
      toast.error(`Minimum ${minW.toLocaleString('fr-CI')} FCFA`);
      return;
    }
    setSubmitting(true);
    try {
      await onWithdraw({ ...withdrawForm, amount: parseInt(withdrawForm.amount) });
      setShowWithdrawForm(false);
      setWithdrawForm({ amount: '', operator: '', phone: '' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 mb-5">
      <h3 className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3"
        style={{ fontFamily: "'Rajdhani', sans-serif" }}>
        <FaGift className="inline mr-2" />
        PARRAINAGE
      </h3>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="card text-center py-3">
          <div className="text-xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {referral.stats?.total_filleuls ?? 0}
          </div>
          <div className="text-white/50 text-xs mt-0.5">Filleuls</div>
        </div>
        <div className="card text-center py-3">
          <div className="text-xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {referral.stats?.total_premium ?? 0}
          </div>
          <div className="text-white/50 text-xs mt-0.5">Premium</div>
        </div>
        <div className="card text-center py-3">
          <div className="text-xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {(referral.stats?.total_earned_fcfa ?? 0).toLocaleString('fr-CI')}
          </div>
          <div className="text-white/50 text-xs mt-0.5">FCFA gagnés</div>
        </div>
      </div>

      {/* Solde + bouton retrait */}
      <div
        className="rounded-2xl p-4 mb-3"
        style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}
      >
        <p className="text-white/50 text-xs mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          SOLDE DISPONIBLE
        </p>
        <div className="flex items-center justify-between">
          <span className="text-3xl font-black text-green-400" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {balance.toLocaleString('fr-CI')} <span className="text-lg">FCFA</span>
          </span>
          <button
            onClick={() => setShowWithdrawForm(true)}
            disabled={!canWithdraw}
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-black text-sm uppercase disabled:opacity-30 transition-opacity"
            style={{
              background: canWithdraw ? 'linear-gradient(135deg, ##003DA5, #15803d)' : 'rgba(255,255,255,0.08)',
              color: canWithdraw ? 'white' : 'rgba(255,255,255,0.3)',
              fontFamily: "'Rajdhani', sans-serif",
            }}
          >
            <FaWallet size={13} />
            RETIRER
          </button>
        </div>
        {!canWithdraw && (
          <p className="text-white/30 text-xs mt-2">
            Minimum {minW.toLocaleString('fr-CI')} FCFA pour retirer
          </p>
        )}
      </div>

      {/* Code de parrainage */}
      <div
        className="rounded-2xl p-4 mb-3"
        style={{ background: 'rgba(255,184,28,0.06)', border: '1px solid rgba(255,184,28,0.2)' }}
      >
        <p className="text-white/50 text-xs mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>TON CODE</p>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-black text-qfl-gold tracking-widest flex-1"
            style={{ fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.12em' }}>
            {referral.referralCode}
          </span>
          <button
            onClick={() => { navigator.clipboard.writeText(referral.referralCode); toast.success('Code copié !'); }}
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(255,184,28,0.15)', color: '#FFB81C' }}
          >
            <FaCopy size={13} />
          </button>
          <button
            onClick={() => {
              const link = `${window.location.origin}/register?ref=${referral.referralCode}`;
              if (navigator.share) {
                navigator.share({
                  title: 'OM QUIZ',
                  text: `🏆 Rejoins OM QUIZ avec mon code ${referral.referralCode} !`,
                  url: link,
                });
              } else {
                navigator.clipboard.writeText(link);
                toast.success('Lien copié !');
              }
            }}
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(255,184,28,0.15)', color: '#FFB81C' }}
          >
            <FaShareAlt size={13} />
          </button>
        </div>
        <p className="text-white/30 text-xs mt-2">
          Mensuel → <span className="text-qfl-gold font-bold">200 FCFA</span> · Annuel → <span className="text-qfl-gold font-bold">2 000 FCFA</span> quand ton filleul passe Premium
        </p>
      </div>

      {/* Liste des filleuls */}
      {referral.filleuls?.length > 0 && (
        <div className="flex flex-col gap-2 mb-3">
          {referral.filleuls.map((f) => (
            <div key={f.pseudo} className="flex items-center gap-3 card py-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
                style={{ background: 'rgba(255,184,28,0.15)', color: '#FFB81C', fontFamily: "'Rajdhani', sans-serif" }}>
                {f.pseudo[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{f.pseudo}</p>
                <p className="text-white/40 text-xs">Niveau {f.level}</p>
              </div>
              {f.activated_at ? (
                <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-green-500/20 text-green-400"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  +{(f.commission_fcfa ?? 0).toLocaleString('fr-CI')} FCFA
                </span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/30"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  Inscrit
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Historique des retraits */}
      {referral.withdrawals?.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-white/40 text-xs uppercase tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Retraits récents
          </p>
          {referral.withdrawals.map((w) => (
            <div key={w.id} className="flex items-center gap-3 card py-2.5">
              <div className="flex-1">
                <p className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {w.amount_fcfa.toLocaleString('fr-CI')} FCFA
                </p>
                <p className="text-white/40 text-xs">{w.operator.replace('_', ' ')} · {w.phone}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                w.status === 'paid'     ? 'bg-green-500/20 text-green-400' :
                w.status === 'rejected' ? 'bg-red-500/20 text-red-400'    :
                'bg-yellow-500/20 text-yellow-400'
              }`} style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {w.status === 'paid' ? 'Payé' : w.status === 'rejected' ? 'Rejeté' : 'En attente'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Modal formulaire de retrait */}
      <AnimatePresence>
        {showWithdrawForm && (
          <motion.div className="fixed inset-0 z-50 flex items-end justify-center"
            style={{ paddingBottom: 80 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowWithdrawForm(false)} />
            <motion.div
              className="relative w-full max-w-md rounded-3xl p-6"
              style={{ background: '#001a4d', border: '1px solid rgba(34,197,94,0.3)' }}
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-black text-white uppercase tracking-wider"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  <FaWallet className="inline mr-2 text-green-400" />
                  DEMANDE DE RETRAIT
                </h3>
                <button onClick={() => setShowWithdrawForm(false)} className="text-white/40 hover:text-white/70">
                  <FaTimes size={16} />
                </button>
              </div>

              <div className="text-white/50 text-xs mb-4 text-center"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Solde disponible : <span className="text-green-400 font-black text-base">
                  {balance.toLocaleString('fr-CI')} FCFA
                </span>
              </div>

              <form onSubmit={handleWithdraw} className="flex flex-col gap-3">
                <div>
                  <label className="text-white/50 text-xs mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    MONTANT (FCFA) — min. {minW.toLocaleString('fr-CI')}
                  </label>
                  <input
                    type="number"
                    min={minW}
                    max={balance}
                    value={withdrawForm.amount}
                    onChange={(e) => setWithdrawForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder={`${minW}`}
                    className="form-input w-full"
                    required
                  />
                </div>

                <div>
                  <label className="text-white/50 text-xs mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    OPÉRATEUR
                  </label>
                  <select
                    value={withdrawForm.operator}
                    onChange={(e) => setWithdrawForm((f) => ({ ...f, operator: e.target.value }))}
                    className="form-input w-full"
                    required
                  >
                    <option value="">-- Choisir --</option>
                    {OPERATORS.map((op) => (
                      <option key={op.key} value={op.key}>{op.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-white/50 text-xs mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    NUMÉRO DE TÉLÉPHONE
                  </label>
                  <input
                    type="tel"
                    value={withdrawForm.phone}
                    onChange={(e) => setWithdrawForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="07 XX XX XX XX"
                    className="form-input w-full"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-xl font-black text-sm uppercase tracking-widest mt-2 disabled:opacity-50 transition-opacity"
                  style={{
                    background: 'linear-gradient(135deg, ##003DA5, #15803d)',
                    color: 'white',
                    fontFamily: "'Rajdhani', sans-serif",
                  }}
                >
                  {submitting ? 'ENVOI...' : 'CONFIRMER LE RETRAIT'}
                </button>
                <p className="text-white/30 text-xs text-center">
                  Traitement sous 48h ouvrables
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ReferralSection;

